/**
 * BLRRD Editor — i18n primitive.
 *
 * Pure-function lookup, no framework. Locales are JSON-imported at build
 * time so `t()` is synchronous (matches the legacy editor's call style).
 *
 * Public surface:
 *   t(key, vars?)         → string lookup with {var} interpolation
 *   setLocale(locale)     → switches active locale, walks DOM, persists
 *   getLocale()           → 'de' | 'en'
 *   getSupportedLocales() → ['de', 'en']
 *   formatNumber(n, opts) → Intl.NumberFormat for the active locale
 *   applyDOM(root)        → walks [data-i18n] / [data-i18n-attr] in subtree
 *   onLocaleChange(fn)    → subscribe; returns unsubscribe
 *   initI18n()            → load preference (localStorage > navigator.language > de)
 *
 * Keys missing in the active locale fall back to the de dictionary; missing
 * in both returns the key string itself (loud-fail so QA notices).
 */

import de from './locales/de.json';
import en from './locales/en.json';

const DICT       = { de, en };
const SUPPORTED  = ['de', 'en'];
const FALLBACK   = 'de';
const STORAGE_KEY = 'blrrd_locale';

let activeLocale = FALLBACK;
const listeners = new Set();

export function getLocale() { return activeLocale; }

export function getSupportedLocales() { return SUPPORTED.slice(); }

export function t(key, vars) {
    if (!key) return '';
    const raw = lookup(DICT[activeLocale], key);
    const value = raw != null ? raw : lookup(DICT[FALLBACK], key);
    if (value == null) return key;
    return vars ? interpolate(value, vars) : value;
}

export function setLocale(locale, { persist = true } = {}) {
    if (!SUPPORTED.includes(locale)) return;
    if (locale === activeLocale) return;
    activeLocale = locale;
    if (persist) {
        try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* private mode */ }
    }
    if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = locale;
        applyDOM(document);
    }
    listeners.forEach((fn) => { try { fn(locale); } catch { /* listener bug — non-fatal */ } });
}

export function onLocaleChange(fn) {
    if (typeof fn !== 'function') return () => {};
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function formatNumber(n, opts = {}) {
    const tag = activeLocale === 'de' ? 'de-DE' : 'en-US';
    try { return new Intl.NumberFormat(tag, opts).format(n); }
    catch { return String(n); }
}

export function applyDOM(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('[data-i18n]').forEach((el) => {
        const key = el.getAttribute('data-i18n');
        if (!key) return;
        const value = t(key);
        if (value != null) el.textContent = value;
    });
    root.querySelectorAll('[data-i18n-attr]').forEach((el) => {
        const spec = el.getAttribute('data-i18n-attr');
        if (!spec) return;
        /* Format: "attr:key;attr2:key2" — e.g. "title:tools.crop;aria-label:tools.cropAria" */
        spec.split(';').forEach((part) => {
            const colon = part.indexOf(':');
            if (colon < 0) return;
            const attr = part.slice(0, colon).trim();
            const key  = part.slice(colon + 1).trim();
            if (!attr || !key) return;
            el.setAttribute(attr, t(key));
        });
    });
}

export function initI18n() {
    let preferred = null;
    try { preferred = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    if (!preferred || !SUPPORTED.includes(preferred)) {
        const navLang = (typeof navigator !== 'undefined' && navigator.language) || FALLBACK;
        preferred = navLang.toLowerCase().startsWith('en') ? 'en' : 'de';
    }
    /* setLocale early-returns when locale matches activeLocale; force-apply
     * the DOM walk on first init even if preferred === FALLBACK. */
    if (preferred === activeLocale) {
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.lang = activeLocale;
            applyDOM(document);
        }
    } else {
        setLocale(preferred, { persist: false });
    }
}

function lookup(dict, key) {
    if (!dict) return undefined;
    return key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), dict);
}

function interpolate(s, vars) {
    return s.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : '{' + k + '}'));
}
